import DatePicker from '@mui/lab/DatePicker';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import useWindowDimensions from '../../../../hooks/useWindowDimensions';
import NotationAttributeContext from '../../../../shared-components/contexts/NotationAttributesContext';
import UserContext from '../../../../shared-components/contexts/UserContext';
import OutlinedFormControl from '../../../../shared-components/OutlinedFormControl';
import UserSearch from '../../../../shared-components/UserSearch';
import { ICategory } from '../../../../shared-interfaces/Notation/ICategory';
import { ICoreUser } from '../../../../shared-interfaces/User/ICoreUser';
import { IUser } from '../../../../shared-interfaces/User/IUser';
import { AlertStatus } from '../../../../shared-types/Notation/AlertStatus';
import {
  CreatedOnTypes,
  notationTypes,
  PeopleTypes,
} from '../../../../shared-types/Notation/NotationFilterTypes';
import { NotationType } from '../../../../shared-types/Notation/NotationType';
import { Role } from '../../../../shared-types/Roles';
import INotationFilters, {
  emptyNotationFilters,
} from '../../Interfaces/INotationFilters';
import useToggle from '../../../../hooks/useToggle';
import LaunchDarklyContext from '../../../../shared-components/contexts/LaunchDarklyContext';

const useStyles = makeStyles(theme => ({
  userSelectContainer: {
    margin: '0 8px 7px 0',
  },
  radioButton: {
    padding: '9px 3px 9px 9px',
  },
  radioLabel: {
    marginRight: '30px',
  },
}));

interface INotationFilterPopverContentProps {
  open: boolean;
  close: () => void;
  notationFilters: INotationFilters;
  onApply: (filters: INotationFilters) => void;
}

const pseudoCategories: ICategory[] = [
  {
    id: -2,
    institutionId: -1,
    name: 'All Categories',
    deleted: false,
    isActive: true,
    createdById: '',
    createdOn: new Date(),
    modifiedOn: new Date(),
  },
  {
    id: -1,
    institutionId: -1,
    name: 'No Category',
    deleted: false,
    isActive: true,
    createdById: '',
    createdOn: new Date(),
    modifiedOn: new Date(),
  },
];

const NotationFilterPopoverContent = (
  props: INotationFilterPopverContentProps,
) => {
  const launchDarkly = useContext(LaunchDarklyContext);
  const user = useContext(UserContext);
  const notationContext = useContext(NotationAttributeContext);
  const classes = useStyles();
  const { height } = useWindowDimensions();

  const [selectAll, setSelectAll] = useToggle(true);
  const [notationTypeFilters, setNotationTypeFilters] = useState<string[]>(
    mapPropsToNotationTypes(props.notationFilters),
  );

  const categories = [...pseudoCategories, ...notationContext.categories];
  const [category, setCategory] = useState(
    props.notationFilters.category ?? categories.find(c => c.id === -2),
  );

  const [assignedToCollapse, setAssignedToCollapse] = useState<PeopleTypes>(
    mapUserToUserSelect(
      props.notationFilters.assignedTo,
      props.notationFilters.onlyUnassigned,
    ),
  );
  const [assignedTo, setAssignedTo] = useState<ICoreUser | undefined>(
    assignedToCollapse === 'other'
      ? (props.notationFilters.assignedTo as ICoreUser)
      : undefined,
  );

  const [createdByCollapse, setCreatedByCollapse] = useState<PeopleTypes>(
    mapUserToUserSelect(props.notationFilters.createdBy),
  );
  const [createdBy, setCreatedBy] = useState<ICoreUser | undefined>(
    createdByCollapse === 'other'
      ? (props.notationFilters.createdBy as ICoreUser)
      : undefined,
  );

  const [createdForCollapse, setCreatedForCollapse] = useState<PeopleTypes>(
    props.notationFilters.studentId ? 'other' : 'none',
  );

  const [createdFor, setCreatedFor] = useState<ICoreUser | undefined>(
    props.notationFilters.studentId as ICoreUser,
  );

  const [createdOnValue, setCreatedOnValue] = useState<CreatedOnTypes>(
    mapDateToCreatedOn(
      props.notationFilters.fromDate,
      props.notationFilters.toDate,
    ),
  );
  const [customFromDate, setCustomFromDate] = useState<Date | undefined>(
    new Date(),
  );
  const [customToDate, setCustomToDate] = useState<Date | undefined>(
    props.notationFilters.toDate,
  );

  function formatFilters(): INotationFilters {
    var notationTypes: NotationType[] = [];
    var alertStatuses: AlertStatus[] = [];

    if (
      notationTypeFilters.findIndex(x => x === 'Alert:Open') > -1 ||
      notationTypeFilters.findIndex(x => x === 'Alert:Lowered') > -1
    ) {
      notationTypes.push(NotationType.Alert);

      if (notationTypeFilters.findIndex(x => x === 'Alert:Open') > -1) {
        alertStatuses.push(AlertStatus.Open);
      }

      if (notationTypeFilters.findIndex(x => x === 'Alert:Lowered') > -1) {
        alertStatuses.push(AlertStatus.Lowered);
      }
    }

    notationTypeFilters.forEach(n => {
      if (n !== 'Alert:Open' && n !== 'Alert:Lowered') {
        notationTypes.push(n as NotationType);
      }
    });

    if (category && category.id === -2) setCategory(undefined);

    const createdOn =
      createdOnValue === 'all'
        ? dayjs('1/1/1970')
        : createdOnValue === 'sevenDays'
        ? dayjs().startOf('d').subtract(7, 'd')
        : createdOnValue === 'thirtyDays'
        ? dayjs().startOf('d').subtract(30, 'd')
        : createdOnValue === 'sixMonths'
        ? dayjs().startOf('M').subtract(6, 'M')
        : undefined;

    return {
      notationTypes: notationTypes,
      alertStatus: alertStatuses,
      category: category && category.id > -2 ? category : undefined,
      fromDate:
        createdOnValue === 'custom' ? customFromDate : createdOn?.toDate(),
      toDate: createdOnValue === 'custom' ? customToDate : undefined,
      createdBy:
        createdByCollapse === 'me'
          ? user
          : createdByCollapse === 'other'
          ? createdBy
          : undefined,
      studentId: createdForCollapse === 'other' ? createdFor : undefined,
      assignedTo:
        assignedToCollapse === 'me'
          ? user
          : assignedToCollapse === 'other'
          ? assignedTo
          : assignedToCollapse === 'unassigned'
          ? undefined
          : undefined,
      onlyUnassigned: assignedToCollapse === 'unassigned',
    };
  }

  function onNotationSelectAll() {
    if (!selectAll) {
      setNotationTypeFilters([
        'Alert:Open',
        'Alert:Lowered',
        NotationType.Update,
        NotationType.Encouragement,
        NotationType.DataInsight,
      ]);
    } else {
      setNotationTypeFilters([]);
    }
    setSelectAll();
  }

  function onNotationTypeSelect(type: string[]) {
    const filteredTypes = type.filter(s => s !== undefined);

    setNotationTypeFilters(filteredTypes);
    if (filteredTypes.length === 0) {
      setSelectAll(false);
      setNotationTypeFilters([]);
    } else if (filteredTypes.length >= 5) {
      setNotationTypeFilters([
        'Alert:Open',
        'Alert:Lowered',
        NotationType.Update,
        NotationType.Encouragement,
        NotationType.DataInsight,
      ]);
      setSelectAll(true);
    }
  }

  return (
    <>
      <Box
        style={
          height < 900
            ? {
                marginBottom: '10px',
                maxHeight: '300px',
                overflowY: 'scroll',
                overflowX: 'hidden',
              }
            : { marginBottom: '10px' }
        }
      >
        <FormControl
          variant="outlined"
          style={{ width: '100%', marginTop: '5px' }}
        >
          <InputLabel id="notation-type-label">Notation Type</InputLabel>
          <Select
            labelId="notation-type-label"
            id="notation-type"
            inputProps={{
              'aria-labelledby': 'notation-type-label',
            }}
            value={notationTypeFilters}
            label="Notation Type"
            multiple
            renderValue={selected => {
              if ((selected as string[]).length === 5)
                return 'All Notation Types';
              else
                return (selected as string[])
                  .map(s => notationTypes.find(x => x.value === s)?.label)
                  .join(', ');
            }}
            onChange={e => {
              onNotationTypeSelect(e.target.value as string[]);
            }}
          >
            {launchDarkly['filters-select-deselect-all'] && (
              <MenuItem
                style={{
                  borderBottom: '2px solid #dcdcdc',
                  backgroundColor: '#fff',
                }}
                onClick={(e: any) => {
                  onNotationSelectAll();
                  e.stopImmediatePropigation();
                }}
                id="select-all-checkbox"
              >
                <ListItemIcon>
                  <Checkbox
                    color="primary"
                    icon={<RadioButtonUncheckedIcon />}
                    checkedIcon={<CheckCircleIcon />}
                    checked={selectAll}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={`${selectAll ? 'Deselect' : 'Select'} All`}
                />
              </MenuItem>
            )}
            {notationTypes.map(n => (
              <MenuItem
                key={n.value}
                value={n.value}
                style={{ backgroundColor: '#fff' }}
              >
                <ListItemIcon>
                  <Checkbox
                    color="primary"
                    checked={notationTypeFilters.indexOf(n.value) > -1}
                  />
                </ListItemIcon>
                <ListItemText primary={n.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          variant="outlined"
          style={{ width: '100%', marginTop: '10px' }}
        >
          <InputLabel id="category-label">Category</InputLabel>
          <Select
            labelId="category-label"
            id="category"
            inputProps={{
              'aria-labelledby': 'category-label',
            }}
            value={category}
            renderValue={selected => (selected as ICategory).name}
            label="Category"
            autoWidth={false}
          >
            {categories.map(x => (
              <MenuItem
                key={x.id}
                value={x.id}
                onClick={e => {
                  setCategory(x);
                }}
              >
                {x.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl
          variant="outlined"
          style={{ width: '100%', marginTop: '10px' }}
        >
          <InputLabel id="created-on-label">Created On</InputLabel>
          <Select
            labelId="created-on-label"
            id="created-on"
            inputProps={{
              'aria-labelledby': 'created-on-label',
            }}
            value={createdOnValue ?? ''}
            label="Created On"
            autoWidth={false}
            onChange={e => {
              setCreatedOnValue(e.target.value as CreatedOnTypes);
            }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="sevenDays">Past 7 Days</MenuItem>
            <MenuItem value="thirtyDays">Past 30 Days</MenuItem>
            <MenuItem value="sixMonths">Past 6 Months</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
          </Select>

          <Collapse in={createdOnValue === 'custom'}>
            <Box
              className={classes.userSelectContainer}
              style={{ marginTop: '10px' }}
            >
              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <DatePicker
                    clearable
                    label="Start Date"
                    value={customFromDate ?? null}
                    maxDate={new Date()}
                    renderInput={props => (
                      <TextField
                        inputProps={{ 'aria-label': 'Start Date' }}
                        {...props}
                      />
                    )}
                    onChange={date => {
                      //@ts-ignore
                      setCustomFromDate(date);
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <DatePicker
                    clearable
                    label="End Date"
                    value={customToDate ?? null}
                    minDate={customFromDate ?? undefined}
                    maxDate={new Date()}
                    renderInput={props => (
                      <TextField
                        inputProps={{ 'aria-label': 'End Date' }}
                        {...props}
                      />
                    )}
                    onChange={date => {
                      //@ts-ignore
                      setCustomToDate(date);
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </FormControl>

        <OutlinedFormControl
          label="Assigned To"
          style={{ width: '100%', marginTop: '10px' }}
        >
          <RadioGroup
            value={assignedToCollapse}
            onChange={e => setAssignedToCollapse(e.target.value as PeopleTypes)}
            row
          >
            <FormControlLabel
              value="Anyone"
              control={
                <Radio
                  value="none"
                  color="primary"
                  className={classes.radioButton}
                />
              }
              label="Anyone"
              className={classes.radioLabel}
            />
            <FormControlLabel
              value="Me"
              control={
                <Radio
                  value="me"
                  color="primary"
                  className={classes.radioButton}
                />
              }
              label="Me"
              className={classes.radioLabel}
            />
            <FormControlLabel
              value="Unassigned"
              control={
                <Radio
                  value="unassigned"
                  color="primary"
                  className={classes.radioButton}
                />
              }
              label="Unassigned"
              className={classes.radioLabel}
            />
            <FormControlLabel
              value="Other"
              control={
                <Radio
                  value="other"
                  color="primary"
                  className={classes.radioButton}
                />
              }
              label="Other"
              className={classes.radioLabel}
            />
          </RadioGroup>
          <Collapse in={assignedToCollapse === 'other'}>
            <Box className={classes.userSelectContainer}>
              <UserSearch
                label="Search for a User"
                roleRestriction={[
                  Role.PeerAdvisor,
                  Role.Advisor,
                  Role.Generalist,
                  Role.Executive,
                  Role.Administrator,
                ]}
                value={assignedTo as ICoreUser}
                onSelect={u => setAssignedTo(u as ICoreUser)}
              />
            </Box>
          </Collapse>
        </OutlinedFormControl>

        <OutlinedFormControl
          label="Created By"
          style={{ width: '100%', marginTop: '10px' }}
        >
          <RadioGroup
            value={createdByCollapse}
            onChange={e => setCreatedByCollapse(e.target.value as PeopleTypes)}
            row
          >
            <FormControlLabel
              value="Anyone"
              control={
                <Radio
                  value="none"
                  color="primary"
                  className={classes.radioButton}
                />
              }
              label="Anyone"
              className={classes.radioLabel}
            />
            <FormControlLabel
              value="Me"
              control={
                <Radio
                  value="me"
                  color="primary"
                  className={classes.radioButton}
                />
              }
              label="Me"
              className={classes.radioLabel}
            />
            <FormControlLabel
              value="Other"
              control={
                <Radio
                  value="other"
                  color="primary"
                  className={classes.radioButton}
                />
              }
              label="Other"
              className={classes.radioLabel}
            />
          </RadioGroup>

          <Collapse in={createdByCollapse === 'other'}>
            <Box className={classes.userSelectContainer}>
              <UserSearch
                label="Search for a User"
                roleRestriction={[
                  Role.PeerAdvisor,
                  Role.Advisor,
                  Role.Generalist,
                  Role.Executive,
                  Role.Administrator,
                ]}
                value={createdBy as ICoreUser}
                onSelect={u => setCreatedBy(u as ICoreUser)}
              />
            </Box>
          </Collapse>
        </OutlinedFormControl>

        <OutlinedFormControl
          label="Created For"
          style={{ width: '100%', marginTop: '10px' }}
        >
          <RadioGroup
            value={createdForCollapse}
            onChange={e => setCreatedForCollapse(e.target.value as PeopleTypes)}
            row
          >
            <FormControlLabel
              value="Anyone"
              control={
                <Radio
                  value="none"
                  color="primary"
                  className={classes.radioButton}
                />
              }
              label="Anyone"
              className={classes.radioLabel}
            />
            <FormControlLabel
              value="Other"
              control={
                <Radio
                  value="other"
                  color="primary"
                  className={classes.radioButton}
                />
              }
              label="Other"
              className={classes.radioLabel}
            />
          </RadioGroup>

          <Collapse in={createdForCollapse === 'other'}>
            <Box className={classes.userSelectContainer}>
              <UserSearch
                label="Search for a User"
                roleRestriction={[Role.Student, Role.PeerAdvisor]}
                value={createdFor as ICoreUser}
                onSelect={u => setCreatedFor(u as ICoreUser)}
              />
            </Box>
          </Collapse>
        </OutlinedFormControl>
      </Box>

      <Box display="flex">
        <Box sx={{ flexGrow: 1 }}>
          <Button
            variant="outlined"
            onClick={() => {
              props.onApply(emptyNotationFilters());
              props.close();
            }}
          >
            Reset Filters
          </Button>
        </Box>

        <Box>
          <Button
            variant="text"
            color="primary"
            onClick={() => {
              props.close();
            }}
            style={{ marginRight: '5px' }}
          >
            Cancel
          </Button>

          <Button
            color="primary"
            onClick={() => {
              props.onApply(formatFilters());
              props.close();
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </>
  );
};

const mapPropsToNotationTypes = (filter: INotationFilters): string[] => {
  var f: string[] = [];
  if (filter.notationTypes.length === 0) {
    return [
      'Alert:Open',
      'Alert:Lowered',
      NotationType.Update,
      NotationType.Encouragement,
      NotationType.DataInsight,
    ];
  }
  if (filter.alertStatus.findIndex(a => a === AlertStatus.Open) > -1) {
    f.push('Alert:Open');
  }
  if (filter.alertStatus.findIndex(a => a === AlertStatus.Lowered) > -1) {
    f.push('Alert:Lowered');
  }

  filter.notationTypes.forEach(n => {
    if (n !== NotationType.Alert) {
      f.push(n);
    }
  });

  return f;
};

const mapUserToUserSelect = (
  user?: IUser | ICoreUser,
  onlyUnassigned?: boolean,
): PeopleTypes => {
  const currentUser = useContext(UserContext);
  if (onlyUnassigned) {
    return 'unassigned';
  } else if (!user) return 'none';
  return currentUser.id === user.id ? 'me' : 'other';
};

const mapDateToCreatedOn = (
  fromDate: Date | undefined,
  toDate: Date | undefined,
): CreatedOnTypes => {
  const dateInDayjs = dayjs(fromDate);

  switch (true) {
    case fromDate === undefined:
      return 'all';
    case toDate !== undefined:
      return 'custom';
    case dateInDayjs.isSame(dayjs().startOf('M').subtract(6, 'M')):
      return 'sixMonths';
    case dateInDayjs.isSame('1/1/1970'):
      return 'all';
    case dateInDayjs.isSame(dayjs().startOf('d').subtract(7, 'd')):
      return 'sevenDays';
    case dateInDayjs.isSame(dayjs().startOf('d').subtract(30, 'd')):
      return 'thirtyDays';
    default:
      return 'custom';
  }
};

export default NotationFilterPopoverContent;
